import create from '../creator'
import axios from '../../axios-setup'
import pickUpTime from '@/utils/pickUpTime'
import toGoOrderingTime from '@/utils/toGoOrderingTime'
import forHereOrderingTime from '@/utils/forHereOrderingTime'
import { tz } from 'moment'
import { getCurrentDow, getValidationCode, isDesktop } from '@/utils/helpers'
import { isEmpty } from 'lodash'
import tableSideOrderingTime from '@/utils/tableSideOrderingTime'
import storage from '@/utils/storage'
import { getTimeZoneName } from '@/utils/dateUtils'
import { VALIDATION_MESSAGES } from '@/constants'
import { isVersionSameOrHigher } from '@/utils/helpersTS'

const initialState = () => ({
	venueInfo: {
		address: '',
		phone: '',
		email: '',
		acceptOnlineOrder: true,
		acceptToGo: true,
		acceptForHere: true,
		acceptPickUp: true,
		businessDays: {},
		gallery: [],
		id: '',
		localId: null,
		logoUrl: '',
		name: '',
		openHours: [],
		orderingHours: [],
		toGoHours: [],
		forHereHours: [],
		opened: false,
		pickupHours: [],
		rating: null,
		settings: {},
		smartTabFee: null,
		timeZone: '',
		venueTypes: [],
		webVersion: ''
	},
	qrInfo: {},
	pickUpHours: [],
	toGoHours: [],
	forHereHours: [],
	pickUpTime: {},
	currentPickupAvailablePeriods: [],
	isForHereOrderingAvailable: false,
	isToGoOrderingAvailable: false,
	isPickUpOrderingAvailable: false,
	currentDow: 1,
	currentTime: '',
	isLoading: false
})
const state = initialState()

const actions = {
	async getVenueInfo({ commit }, { venueChainSubdomain, venueId }) {
		commit('getVenueInfo')

		try {
			const resp = await axios.get(
				`venue/${venueChainSubdomain}/${venueId}`,
				{},
				{ skipError: true }
			)
			commit('getVenueInfoSuccess', resp.data)

			storage.setStorage('lastVenueChainSubdomain', venueChainSubdomain)
			storage.setStorage('lastVenueId', venueId)

			return resp.data
		} catch (e) {
			const validationCode = getValidationCode(e)

			if (validationCode === VALIDATION_MESSAGES.VENUE_NOT_FOUND) {
				storage.removeFromStorage('lastVenueChainSubdomain')
				storage.removeFromStorage('lastVenueId')
			}

			commit('getVenueInfoError')
			throw e
		}
	},

	async getQrInfo({ commit }, qrCodeId) {
		const resp = await axios.get(`qrCodeTable/${qrCodeId}`)
		commit('setQrInfo', resp.data)
		return resp.data
	},

	setPickUpTimeWithoutGap({ commit, state }) {
		const data = pickUpTime.getPickUpHoursTime(state.venueInfo, 0)

		commit('setPickUpTime', data)
	},

	setIsTableSideOrderingAvailable({ commit, state }) {
		const data = tableSideOrderingTime.checkIsAvailable(state.venueInfo)
		commit('setIsTableSideOrderingAvailable', data)
	},

	setAllOrderingAvailable({ commit, state }, isSetPickUpWithOutGap = false) {
		const isToGoAvailable = toGoOrderingTime.checkIsAvailable(state.venueInfo)
		const isForHereAvailable = forHereOrderingTime.checkIsAvailable(
			state.venueInfo
		)

		const {
			periods: toGoHours,
			isToGoNotAvailableNowAndAvailableToday
		} = toGoOrderingTime.getClosestAvailableTime(state.venueInfo)

		const {
			periods: forHereHours,
			isForHereNotAvailableNowAndAvailableToday
		} = forHereOrderingTime.getClosestAvailableTime(state.venueInfo)

		const pickUpTimeData = isSetPickUpWithOutGap
			? pickUpTime.getPickUpHoursTime(state.venueInfo, 0)
			: pickUpTime.getPickUpHoursTime(state.venueInfo)
		const isPickUpOrderingAvailable =
			!isEmpty(pickUpTimeData.pickUpTime) && state.venueInfo.acceptPickUp

		const { currentDow } = getCurrentDow()

		const payload = {
			isToGoOrderingAvailable: isToGoAvailable && state.venueInfo.acceptToGo,
			isForHereOrderingAvailable:
				isForHereAvailable && state.venueInfo.acceptForHere,
			pickUpHours: pickUpTimeData.pickUpHours || [],
			pickUpTime: pickUpTimeData.pickUpTime,
			currentPickupAvailablePeriods:
				pickUpTimeData.currentPickupAvailablePeriods,
			isPickUpOrderingAvailable,
			currentDow,
			toGoHours,
			forHereHours,
			isForHereNotAvailableNowAndAvailableToday,
			isToGoNotAvailableNowAndAvailableToday
		}

		commit('setAllOrderingAvailable', payload)
	}
}

const mutations = {
	setAllOrderingAvailable(state, payload) {
		Object.assign(state, payload)
	},

	getVenueInfo(state) {
		state.isLoading = true
	},

	getVenueInfoError(state) {
		state.isLoading = false
	},

	getVenueInfoSuccess(state, payload) {
		if (!payload.timeZone) {
			// Fallback: sets browser timezone if timezone missing in server response
			payload.timeZone = tz.guess()
		}

		if (payload.settings) {
			const {
				acceptOnlineOrder,
				acceptForHere,
				acceptPickUp
			} = payload.settings
			const isDesktopDevice = isDesktop()

			// For Here OR Pickup must be true in addition to Accept Online Order.
			const isAcceptOnlineOrder = isDesktopDevice
				? acceptOnlineOrder && acceptPickUp
				: acceptOnlineOrder && (acceptForHere || acceptPickUp)

			state.venueInfo = {
				...payload,
				acceptOnlineOrder: isAcceptOnlineOrder,
				acceptToGo: false,
				acceptForHere: acceptForHere,
				acceptPickUp: acceptPickUp
			}
		} else {
			// For ticketing
			state.venueInfo = { ...payload }
		}

		state.isLoading = false
	},

	setQrInfo(state, payload) {
		state.qrInfo = payload
	},

	setPickUpTime(state, payload) {
		state.pickUpHours = payload.pickUpHours
		state.pickUpTime = payload.pickUpTime
	}
}

const getters = {
	venueId(state) {
		return state.venueInfo.id
	},

	timeZoneName: state => getTimeZoneName(state.venueInfo.timeZone),

	venueEmail: state => state.venueInfo.email,

	venueSettings(state) {
		return state.venueInfo.settings
	},

	venueAddress(state) {
		return state.venueInfo.address
	},

	venuePhone(state) {
		return state.venueInfo.phone
	},

	isWebVersionAllowSmoothTransition(state) {
		const {
			venueInfo: { webVersion }
		} = state

		return isVersionSameOrHigher('v.3.8.0', webVersion)
	},

	isWebVersionAllowASAPOption(state) {
		const {
			venueInfo: { webVersion }
		} = state

		return isVersionSameOrHigher('v.4.3.0', webVersion)
	},

	isPickUpTimeAvailable(state) {
		return Object.values(state.pickUpTime).some(timeList => {
			return !!timeList.length
		})
	},

	isOrderingTimeAvailable(state, getters) {
		return (
			state.isToGoOrderingAvailable ||
			state.isForHereOrderingAvailable ||
			getters.isPickUpTimeAvailable
		)
	},

	caPaymentProvider(state) {
		const { settings } = state.venueInfo
		return (
			settings &&
			settings.settingsCreditCard &&
			settings.settingsCreditCard.caPaymentProvider
		)
	},
	coverImage(state) {
		const { gallery } = state.venueInfo

		if (!Array.isArray(gallery) || gallery.length === 0) {
			return null
		}
		return gallery.slice().sort((a, b) => a.position - b.position)[0].url
	}
}

export default create(
	{
		namespaced: true,
		state,
		actions,
		mutations,
		getters
	},
	initialState()
)
